import React, { useState, useEffect } from "react";
import styles from "../../css/SmsDownloadReport.module.css";
import { useForm } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import { apiList } from "../../helpers/apiList";
import { axiosPostApi } from "../../helpers/interceptorServices";
import CustomAlert from "../../helpers/CustomAlert";
import confetti from "canvas-confetti";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

const defaultFormData = {
  lastMonth: "",
  last3Months: "",
  last6Months: "",
  lastYear: "",
  startDate: "",
  endDate: "",
};

const RcsFullStatement = ({ handleopenRcs, handleCloseRcs }) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    register,
    watch,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: defaultFormData,
    mode: "onChange",
  });
  const { user } = useSelector((state) => state.auth);

  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD")); // Initialize to today's date
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD")); // Initialize to today's date
  const [loading, setLoading] = useState(false);

  if (!handleopenRcs) return null;

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period !== "custom") {
      setStartDate(dayjs());
      setEndDate(dayjs());
    }
  };

  const onSubmit = async () => {
    setLoading(true);

    let fromDate = "";
    let toDate = "";

    const today = dayjs();
    const format = "DD-MM-YYYY";

    let period = selectedPeriod;

    if (!period && startDate && endDate) {
      period = "custom";
    }

    switch (period) {
      case "lastMonth":
        fromDate = today.subtract(1, "month").startOf("month").format(format);
        toDate = today.subtract(1, "month").endOf("month").format(format);
        break;
      case "last3Months":
        fromDate = today.subtract(3, "month").startOf("month").format(format);
        toDate = today.endOf("month").format(format);
        break;
      case "last6Months":
        fromDate = today.subtract(6, "month").startOf("month").format(format);
        toDate = today.endOf("month").format(format);
        break;
      case "lastYear":
        fromDate = today.subtract(1, "year").startOf("month").format(format);
        toDate = today.endOf("month").format(format);
        break;
      case "custom":
        if (!startDate || !endDate) {
          CustomAlert({
            title: "Error",
            text: "Please select both start and end dates.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          setLoading(false);
          return;
        }
        fromDate = dayjs(startDate).format(format);
        toDate = dayjs(endDate).format(format);
        break;
      default:
        CustomAlert({
          title: "Error",
          text: "Please select a valid date range.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        setLoading(false);
        return;
    }

    const payload = {
      username: user?.data?.username,
      fromDate,
      toDate,
    };
    axiosPostApi(apiList.RCS_FULL_STATEMENT_HISTORY, payload)
      .then((response) => {
        setLoading(false);
        if (response.status === 200 && response.data.status === true) {
          CustomAlert({
            title: "Success",
            text: response?.data?.message || "Success",
            icon: "success",
            confirmButtonText: "Ok",
          });
          handleCloseRcs();

          const canvas = document.createElement("canvas");
          canvas.style.position = "fixed";
          canvas.style.top = "0";
          canvas.style.left = "0";
          canvas.style.width = "100%";
          canvas.style.height = "100%";
          canvas.style.zIndex = "3000";
          document.body.appendChild(canvas);

          const confettiInstance = confetti.create(canvas, {
            resize: true,
            useWorker: true,
          });

          confettiInstance({
            particleCount: 250,
            spread: 100,
            origin: { y: 0.6 },
          });

          setTimeout(() => {
            document.body.removeChild(canvas);
          }, 3000);
        } else {
          CustomAlert({
            title: "Failed",
            text: response?.data?.message || "Failed",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error:", error);
        CustomAlert({
          title: "Error",
          text: "An error occurred while processing.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.modalOverlay}>
        <div className={styles.modalBoxStatement}>
          {/* Header */}
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>RCS Full Statement</h2>
            <button onClick={handleCloseRcs} className={styles.closeButton}>
              <HighlightOffIcon />
            </button>
          </div>

          {/* Content */}
          <div className={styles.modalContent}>
            <h3 className={styles.questionTitle}>
              For which period do you need a statement?
            </h3>

            {/* Period Options */}
            <div className={styles.periodSection}>
              <div className={styles.periodGrid}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="lastMonth"
                    checked={selectedPeriod === "lastMonth"}
                    onChange={() => handlePeriodChange("lastMonth")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last Month</span>
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="last3Months"
                    checked={selectedPeriod === "last3Months"}
                    onChange={() => handlePeriodChange("last3Months")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last 3 Months</span>
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="last6Months"
                    checked={selectedPeriod === "last6Months"}
                    onChange={() => handlePeriodChange("last6Months")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last 6 Months</span>
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="lastYear"
                    checked={selectedPeriod === "lastYear"}
                    onChange={() => handlePeriodChange("lastYear")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last 1 Year</span>
                </label>
              </div>
            </div>

            {/* Custom Date Section */}
            <div className={styles.customDateSection}>
              <p className={styles.customDateTitle}>
                Or select a custom date of your choice
              </p>

              <div className={styles.dateGrid}>
                <div className={styles.dateField}>
                  <label className={styles.dateLabel}>Start Date</label>
                  <div className={styles.dateInputWrapper}>
                    <input
                      type="date"
                      value={startDate}
                      max={endDate} // Limit start date to end date
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        setStartDate(newStartDate);
                        setSelectedPeriod("custom");
                        // If endDate is before newStartDate, update endDate to newStartDate or today
                        if (
                          newStartDate &&
                          endDate &&
                          dayjs(endDate).isBefore(dayjs(newStartDate))
                        ) {
                          setEndDate(newStartDate); // Set endDate to newStartDate to ensure validity
                        } else if (!endDate) {
                          setEndDate(dayjs().format("YYYY-MM-DD")); // Set to today if end date is empty
                        }
                      }}
                      className={styles.dateInput}
                    />
                  </div>
                </div>

                <div className={styles.dateField}>
                  <label className={styles.dateLabel}>End Date</label>
                  <div className={styles.dateInputWrapper}>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate}
                      max={dayjs().format("YYYY-MM-DD")} // Limit to today
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setSelectedPeriod("custom");
                      }}
                      className={styles.dateInput}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.buttonContainer}>
              <button type="submit" className="btn btn-submit">
                {loading ? (
                  <CircularProgress style={{ width: "20px", height: "20px" }} />
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default RcsFullStatement;
